import type { CveEntry } from "../cve/normalize.js";

/**
 * The Worker's broadcast endpoint.
 *
 * A client no longer fetches NVD, builds a pool or picks a slot entry: it asks
 * what is on air and gets the entry, the slot window and the R2 object that
 * holds the narration. Selection, pinning and (eventually) generation are the
 * Worker's, so a listener needs no model and almost no state.
 */

export const BROADCAST_PATH = "/now/v1";
/**
 * A broadcast request is a small JSON document plus, at most, one background
 * page fetch on the server. Anything past this is a broken path, not a slow
 * one, and must not hold the scheduler.
 */
export const BROADCAST_REQUEST_BUDGET_MS = 4_000;

const DISABLED_VALUES = new Set(["", "0", "false", "off", "none"]);
/** A CVE id someone may name in the path, or in `?cve=`. */
export const TARGETED_ID_PATTERN = /^CVE-\d{4}-\d{4,}$/;
const AUDIO_KEY_PATTERN = /^[a-f0-9]{64}$/;

export interface BroadcastAudio {
  engine: string;
  voice: string;
  /** Content-addressed key, which is also the R2 object name. */
  key: string;
  url: string;
  /** True when the Worker found the clip in R2; false means it is producing it. */
  ready: boolean;
}

export interface BroadcastSlot {
  slotIndex: number;
  slotStartMs: number;
  slotEndMs: number;
  /** The Worker's clock at the moment it answered, for skew-tolerant timing. */
  serverMs: number;
  entry: CveEntry;
  audio: BroadcastAudio;
  pool: { entries: number; complete: boolean; source: string };
}

export interface BroadcastRequestOptions {
  fetchImpl?: typeof fetch;
  signal?: AbortSignal;
  budgetMs?: number;
}

function normalizeOrigin(value: string): string | null {
  try {
    return new URL(value).origin;
  } catch {
    return null;
  }
}

/**
 * Resolve the broadcast origin from `?broadcast=` first, then the build-time
 * setting. Absent or disabled means the station runs the original local path:
 * its own pool, its own picker and its own model.
 */
export function resolveBroadcastOrigin(search: string, configured?: string): string | null {
  let fromQuery: string | null = null;
  try {
    fromQuery = new URLSearchParams(search).get("broadcast");
  } catch {
    fromQuery = null;
  }
  const value = fromQuery ?? configured ?? "";
  if (DISABLED_VALUES.has(value.trim().toLowerCase())) return null;
  return normalizeOrigin(value.trim());
}

/**
 * The CVE named by the URL, if any.
 *
 * `/CVE-2024-3400` is the shareable form, because it reads as a title. `?cve=`
 * exists for the same reason the others do: a build or a test can ask without
 * depending on a rewrite rule being deployed.
 */
export function parseTargetedCve(pathname: string, search: string): string | null {
  let fromQuery: string | null = null;
  try {
    fromQuery = new URLSearchParams(search).get("cve");
  } catch {
    fromQuery = null;
  }
  const segments = pathname.split("/").filter(Boolean);
  const candidate = (fromQuery ?? segments[segments.length - 1] ?? "").trim().toUpperCase();
  return TARGETED_ID_PATTERN.test(candidate) ? candidate : null;
}

export function broadcastUrl(origin: string, slotIndex?: number): string {
  const base = `${origin}${BROADCAST_PATH}`;
  return slotIndex === undefined ? base : `${base}?slot=${slotIndex}`;
}

function positiveNumber(value: unknown): number | null {
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

/**
 * Validate a payload just enough to trust it for playback: a slot we asked
 * about, an entry with text to display, and a key shaped like the cache key.
 * A malformed or unexpected field is a miss, never an exception.
 */
export function parseBroadcastSlot(payload: unknown): BroadcastSlot | null {
  if (!payload || typeof payload !== "object") return null;
  const record = payload as Partial<BroadcastSlot> & { audio?: Partial<BroadcastAudio> };
  const slotIndex = positiveNumber(record.slotIndex);
  const slotStartMs = positiveNumber(record.slotStartMs);
  const slotEndMs = positiveNumber(record.slotEndMs);
  if (slotIndex === null || slotStartMs === null || slotEndMs === null) return null;

  const entry = record.entry as Partial<CveEntry> | undefined;
  if (!entry || typeof entry.id !== "string" || typeof entry.description !== "string") return null;

  const audio = record.audio;
  if (!audio || typeof audio !== "object") return null;
  if (typeof audio.key !== "string" || !AUDIO_KEY_PATTERN.test(audio.key)) return null;

  const pool = record.pool as Partial<BroadcastSlot["pool"]> | undefined;
  return {
    slotIndex,
    slotStartMs,
    slotEndMs,
    serverMs: positiveNumber(record.serverMs) ?? slotStartMs,
    entry: {
      id: entry.id,
      description: entry.description,
      published: entry.published,
      lastModified: entry.lastModified,
      baseScore: entry.baseScore,
      severity: entry.severity,
    },
    audio: {
      engine: typeof audio.engine === "string" ? audio.engine : "",
      voice: typeof audio.voice === "string" ? audio.voice : "",
      key: audio.key,
      url: typeof audio.url === "string" ? audio.url : "",
      ready: audio.ready === true,
    },
    pool: {
      entries: positiveNumber(pool?.entries) ?? 0,
      complete: pool?.complete === true,
      source: typeof pool?.source === "string" ? pool.source : "",
    },
  };
}

/** Ask the Worker what is on air for a slot, or for its current slot. */
export async function requestSlot(
  origin: string,
  slotIndex: number | null,
  options: BroadcastRequestOptions = {},
): Promise<BroadcastSlot | null> {
  return fetchSlot(broadcastUrl(origin, slotIndex ?? undefined), options);
}

/** Ask the Worker for one named disclosure, which is usually not this minute's. */
export async function requestEntry(
  origin: string,
  id: string,
  options: BroadcastRequestOptions = {},
): Promise<BroadcastSlot | null> {
  return fetchSlot(
    `${origin}${BROADCAST_PATH}?cve=${encodeURIComponent(id.toUpperCase())}`,
    options,
  );
}

async function fetchSlot(
  url: string,
  options: BroadcastRequestOptions,
): Promise<BroadcastSlot | null> {
  if (options.signal?.aborted) return null;
  const fetchImpl = options.fetchImpl ?? globalThis.fetch.bind(globalThis);
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), options.budgetMs ?? BROADCAST_REQUEST_BUDGET_MS);
  const forwardAbort = () => controller.abort();
  options.signal?.addEventListener("abort", forwardAbort, { once: true });
  try {
    const response = await fetchImpl(url, {
      cache: "no-store",
      signal: controller.signal,
    });
    if (!response.ok) return null;
    return parseBroadcastSlot(await response.json());
  } catch {
    return null;
  } finally {
    clearTimeout(timer);
    options.signal?.removeEventListener("abort", forwardAbort);
  }
}
